import React, { useState } from 'react';
import { Truck, Download, Maximize2, X, Info, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const SCHEME_SRC = '/assets/route-scheme.jpg';

const content = {
  ru: {
    label: 'Логистика',
    title: 'Схема проезда на производство',
    subtitle: 'г. Астана, ул. Буйрат, зд. 12 — заезд к воротам погрузки',
    steps: [
      'Поверните налево с ул. Буйрат',
      'Заезжайте в первые ворота слева (на здании — «LED Solution»)',
      'Прямо, затем направо к подъёмным воротам № 12',
    ],
    rules: [
      { icon: 'info', text: 'Только боковая загрузка. Задняя невозможна.' },
      { icon: 'phone', text: 'Согласуйте отгрузку минимум за 1 день.' },
      { icon: 'info', text: 'У водителя должны быть ремни для фиксации паллет.' },
      { icon: 'info', text: 'Документы — коричневый контейнер справа от ворот. Нужны паспорт и доверенность.' },
    ],
    contacts: 'Ответственные',
    open: 'Открыть в полном размере',
    download: 'Скачать схему',
    close: 'Закрыть',
  },
  kz: {
    label: 'Логистика',
    title: 'Өндіріске жету схемасы',
    subtitle: 'Астана қ., Буйрат к-сі, ғимарат 12 — тиеу қақпасы',
    steps: [
      'Буйрат көшесінен солға бұрылыңыз',
      'Сол жақтағы бірінші қақпадан кіріңіз (ғимаратта «LED Solution» жазуы)',
      'Тура, содан кейін № 12 көтеру қақпасына қарай оңға',
    ],
    rules: [
      { icon: 'info', text: 'Тек бүйірлік тиеу. Артқы тиеу мүмкін емес.' },
      { icon: 'phone', text: 'Тиеуді кемінде 1 күн бұрын келісіңіз.' },
      { icon: 'info', text: 'Жүргізушіде паллеттерді бекітуге арналған белдіктер болуы керек.' },
      { icon: 'info', text: 'Құжаттар — қақпаның оң жағындағы қоңыр контейнер. Паспорт пен сенімхат қажет.' },
    ],
    contacts: 'Жауаптылар',
    open: 'Толық көлемде ашу',
    download: 'Схеманы жүктеу',
    close: 'Жабу',
  },
  en: {
    label: 'Logistics',
    title: 'Route to the production facility',
    subtitle: 'Astana, Buyrat St., building 12 — loading gates',
    steps: [
      'Turn left from Buyrat Street',
      'Enter the first gate on the left (building marked «LED Solution»)',
      'Go straight, then right to loading gate № 12',
    ],
    rules: [
      { icon: 'info', text: 'Side loading only. Rear loading not possible.' },
      { icon: 'phone', text: 'Coordinate the pick-up at least 1 day in advance.' },
      { icon: 'info', text: 'Driver must have straps to secure the pallets.' },
      { icon: 'info', text: 'Paperwork — brown container to the right of the gate. Passport and power of attorney required.' },
    ],
    contacts: 'Contacts',
    open: 'Open full size',
    download: 'Download scheme',
    close: 'Close',
  },
  ar: {
    label: 'الخدمات اللوجستية',
    title: 'مسار الوصول إلى المصنع',
    subtitle: 'أستانا، شارع بويرات، مبنى 12 — بوابة التحميل',
    steps: [
      'انعطف يساراً من شارع بويرات',
      'ادخل من البوابة الأولى على اليسار (على المبنى مكتوب «LED Solution»)',
      'استمر مباشرة ثم انعطف يميناً إلى بوابة الرفع رقم 12',
    ],
    rules: [
      { icon: 'info', text: 'التحميل من الجانب فقط. التحميل من الخلف غير ممكن.' },
      { icon: 'phone', text: 'يجب تنسيق موعد الشحن قبل يوم واحد على الأقل.' },
      { icon: 'info', text: 'يجب أن يمتلك السائق أحزمة تثبيت المنصات.' },
      { icon: 'info', text: 'الوثائق — الحاوية البنية على يمين البوابة. يلزم جواز السفر والوكالة.' },
    ],
    contacts: 'المسؤولون',
    open: 'فتح بالحجم الكامل',
    download: 'تحميل المخطط',
    close: 'إغلاق',
  },
};

const RouteScheme = () => {
  const { language } = useLanguage();
  const [fullscreen, setFullscreen] = useState(false);
  const t = content[language] || content.en || content.ru;

  return (
    <section id="route" className="py-12 bg-[#061d36] scroll-mt-24" data-testid="route-scheme-section">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#8b4444]/20 text-[#e8b4b4] text-xs font-bold tracking-wider uppercase">
              <Truck className="w-3.5 h-3.5" />
              {t.label}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{t.title}</h2>
          <p className="text-blue-200/70 mb-8">{t.subtitle}</p>

          <div className="grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-6">
            {/* Scheme image */}
            <button
              type="button"
              onClick={() => setFullscreen(true)}
              className="group relative block bg-white rounded-2xl overflow-hidden shadow-xl focus:outline-none focus:ring-2 focus:ring-[#8b4444]"
              data-testid="route-scheme-open"
              aria-label={t.open}
            >
              <img
                src={SCHEME_SRC}
                alt={t.title}
                className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                loading="lazy"
              />
              <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/70 text-white text-xs font-medium backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 className="w-3.5 h-3.5" />
                {t.open}
              </span>
            </button>

            {/* Instructions & rules */}
            <div className="space-y-4">
              <ol className="space-y-3">
                {t.steps.map((step, i) => (
                  <li key={`step-${i}`} className="flex items-start gap-3 bg-white/5 rounded-xl p-3 border border-white/10">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#8b4444] text-white text-sm font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="text-white text-sm leading-snug">{step}</span>
                  </li>
                ))}
              </ol>

              <div className="bg-[#8b4444]/10 border border-[#8b4444]/30 rounded-xl p-4 space-y-2.5">
                {t.rules.map((rule, i) => (
                  <div key={`rule-${i}`} className="flex items-start gap-2.5 text-white/90 text-sm leading-snug">
                    {rule.icon === 'phone' ? (
                      <Phone className="w-4 h-4 text-[#e8b4b4] flex-shrink-0 mt-0.5" />
                    ) : (
                      <Info className="w-4 h-4 text-[#e8b4b4] flex-shrink-0 mt-0.5" />
                    )}
                    <span>{rule.text}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-xs uppercase tracking-wider text-blue-200/60 mb-2">{t.contacts}</p>
                <div className="flex flex-col gap-1 text-white font-medium">
                  <a href="tel:+77717003669" className="flex items-center gap-2 hover:text-[#e8b4b4] transition-colors" dir="ltr" data-testid="route-phone-1">
                    <Phone className="w-4 h-4" /> +7 771 700 36 69
                  </a>
                  <a href="tel:+77711543337" className="flex items-center gap-2 hover:text-[#e8b4b4] transition-colors" dir="ltr" data-testid="route-phone-2">
                    <Phone className="w-4 h-4" /> +7 771 154 33 37
                  </a>
                </div>
              </div>

              <a
                href={SCHEME_SRC}
                download="gipsovinil-route-scheme.jpg"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#8b4444] hover:bg-[#7a3838] text-white font-semibold rounded-xl transition-colors shadow-lg"
                data-testid="route-download-btn"
              >
                <Download className="w-4 h-4" />
                {t.download}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setFullscreen(false)}
          data-testid="route-lightbox"
        >
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label={t.close}
            data-testid="route-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={SCHEME_SRC}
            alt={t.title}
            className="max-w-full max-h-[92vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default RouteScheme;
